import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { SkeletonBlock } from "../../../skeleton-block";
import { useSimulatedLoad } from "../../../use-simulated-load";

import styles from "./charts.module.css";

const PASS_COLOR = "#389e0d";
const PASS_FILL = "#f6ffed";
const PWC_COLOR = "#d46b08";
const PWC_FILL = "#fff7e6";
const FAIL_COLOR = "#cf1322";
const FAIL_FILL = "#fff1f0";
const DURATION_COLOR = "#2d6e57";
const AXIS_COLOR = "#8c8c8c";
const GRID_COLOR = "#e6e6ea";

type VerdictTrendPoint = {
  date: string;
  pass: number;
  passWithComment: number;
  fail: number;
};

/** Static mock verdict trend, roughly centred on the reference KPI split
 * (pass ~78.5%, pass w/ comment ~19.4%, fail ~2.0%) with plausible
 * day-to-day noise. */
const VERDICT_TREND_DATA: VerdictTrendPoint[] = [
  { date: "2026-06-08", pass: 74.1, passWithComment: 22.8, fail: 3.1 },
  { date: "2026-06-10", pass: 76.3, passWithComment: 20.9, fail: 2.8 },
  { date: "2026-06-12", pass: 71.5, passWithComment: 24.6, fail: 3.9 },
  { date: "2026-06-14", pass: 79.8, passWithComment: 18.0, fail: 2.2 },
  { date: "2026-06-16", pass: 82.4, passWithComment: 15.9, fail: 1.7 },
  { date: "2026-06-18", pass: 77.0, passWithComment: 20.1, fail: 2.9 },
  { date: "2026-06-20", pass: 80.6, passWithComment: 17.5, fail: 1.9 },
  { date: "2026-06-22", pass: 75.2, passWithComment: 21.4, fail: 3.4 },
  { date: "2026-06-24", pass: 83.1, passWithComment: 15.2, fail: 1.7 },
  { date: "2026-06-26", pass: 78.9, passWithComment: 19.3, fail: 1.8 },
  { date: "2026-06-28", pass: 81.7, passWithComment: 16.8, fail: 1.5 },
  { date: "2026-06-30", pass: 76.8, passWithComment: 20.7, fail: 2.5 },
  { date: "2026-07-02", pass: 79.4, passWithComment: 18.6, fail: 2.0 },
  { date: "2026-07-04", pass: 84.0, passWithComment: 14.5, fail: 1.5 },
  { date: "2026-07-07", pass: 78.5, passWithComment: 19.4, fail: 2.0 },
];

type DurationPoint = {
  date: string;
  avgDurationMs: number;
};

/** Static mock average run duration, in milliseconds (matches the reference
 * app's field name/shape so the Y axis / tooltip formatters below read the
 * same way). */
const DURATION_DATA: DurationPoint[] = [
  { date: "2026-06-08", avgDurationMs: 268000 },
  { date: "2026-06-10", avgDurationMs: 241000 },
  { date: "2026-06-12", avgDurationMs: 312000 },
  { date: "2026-06-14", avgDurationMs: 287000 },
  { date: "2026-06-16", avgDurationMs: 223000 },
  { date: "2026-06-18", avgDurationMs: 254000 },
  { date: "2026-06-20", avgDurationMs: 296000 },
  { date: "2026-06-22", avgDurationMs: 331000 },
  { date: "2026-06-24", avgDurationMs: 275000 },
  { date: "2026-06-26", avgDurationMs: 209000 },
  { date: "2026-06-28", avgDurationMs: 246000 },
  { date: "2026-06-30", avgDurationMs: 289000 },
  { date: "2026-07-02", avgDurationMs: 263000 },
  { date: "2026-07-04", avgDurationMs: 231000 },
  { date: "2026-07-07", avgDurationMs: 258000 },
];

function formatDateTick(raw: string): string {
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  return parsed.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

function formatSeconds(ms: number): string {
  return `${Math.round(ms / 1000)}s`;
}

/** "Verdict trend" and "Average run duration" charts from the QA
 * automation dashboard, each in its own card. Backed by static mock data —
 * there is no live QA runs source in `platform` yet. */
export function QaAutomationCharts() {
  const isLoading = useSimulatedLoad();

  return (
    <div className={styles.grid}>
      <section className={styles.card}>
        <h3 className={styles.title}>Verdict trend</h3>
        {isLoading ? (
          <SkeletonBlock width="100%" height={260} />
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={VERDICT_TREND_DATA}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11 }}
                stroke={AXIS_COLOR}
                tickFormatter={formatDateTick}
              />
              <YAxis
                stroke={AXIS_COLOR}
                tick={{ fontSize: 11 }}
                domain={[0, 100]}
                tickFormatter={(value) => `${value}%`}
              />
              <Tooltip
                labelFormatter={formatDateTick}
                formatter={(value) => (typeof value === "number" ? formatPercent(value) : value)}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area
                type="monotone"
                dataKey="fail"
                stackId="verdict"
                stroke={FAIL_COLOR}
                fill={FAIL_FILL}
                name="Fail"
                dot={{ r: 3, fill: FAIL_COLOR, strokeWidth: 0 }}
                activeDot={{ r: 5, fill: FAIL_COLOR, strokeWidth: 0 }}
              />
              <Area
                type="monotone"
                dataKey="pass"
                stackId="verdict"
                stroke={PASS_COLOR}
                fill={PASS_FILL}
                name="Pass"
                dot={{ r: 3, fill: PASS_COLOR, strokeWidth: 0 }}
                activeDot={{ r: 5, fill: PASS_COLOR, strokeWidth: 0 }}
              />
              <Area
                type="monotone"
                dataKey="passWithComment"
                stackId="verdict"
                stroke={PWC_COLOR}
                fill={PWC_FILL}
                name="Pass w/ comment"
                dot={{ r: 3, fill: PWC_COLOR, strokeWidth: 0 }}
                activeDot={{ r: 5, fill: PWC_COLOR, strokeWidth: 0 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </section>

      <section className={styles.card}>
        <h3 className={styles.title}>Average run duration</h3>
        {isLoading ? (
          <SkeletonBlock width="100%" height={260} />
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={DURATION_DATA}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11 }}
                stroke={AXIS_COLOR}
                tickFormatter={formatDateTick}
              />
              <YAxis
                stroke={AXIS_COLOR}
                tick={{ fontSize: 11 }}
                tickFormatter={(value) => formatSeconds(value as number)}
              />
              <Tooltip
                labelFormatter={formatDateTick}
                formatter={(value) => (typeof value === "number" ? formatSeconds(value) : value)}
              />
              <Line
                type="monotone"
                dataKey="avgDurationMs"
                stroke={DURATION_COLOR}
                strokeWidth={2}
                dot={{ r: 3, fill: DURATION_COLOR, strokeWidth: 0 }}
                activeDot={{ r: 5, fill: DURATION_COLOR, strokeWidth: 0 }}
                name="Avg duration"
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </section>
    </div>
  );
}
